import { useEffect, useRef, useState } from 'react';

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-32 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-8">
        {/* Tag + Title */}
        <div
          className={`mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block bg-emerald-50 text-emerald-600 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            About Us
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-syne font-bold text-5xl text-gray-900 leading-tight mb-6">
                A Team Focused on
                <br />
                <span className="text-emerald-500">What Matters Most</span>
              </h2>
            </div>
            <div>
              <p className="text-gray-500 text-lg leading-relaxed mb-4">
                FOCUS d.o.o. is a team of seasoned iOS developers and UX specialists united by a shared passion — building software that truly improves people's lives.
              </p>
              <p className="text-gray-500 text-lg leading-relaxed">
                Founded in Croatia and operating internationally, we specialize in creating intuitive, reliable, and privacy-first applications for the Apple ecosystem. Our rigorous development process ensures every product we ship meets the highest standards of performance and security.
              </p>
            </div>
          </div>
        </div>

        {/* Values Row */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {[
            {
              icon: 'ri-focus-3-line',
              title: 'Precision-Driven',
              desc: 'Every line of code is written with intent. We eliminate the unnecessary and amplify what works.',
            },
            {
              icon: 'ri-shield-check-line',
              title: 'Security-First',
              desc: 'User privacy and data protection are baked into the foundation of everything we build.',
            },
            {
              icon: 'ri-user-heart-line',
              title: 'User-Centric',
              desc: 'We obsess over usability so your experience feels effortless from the very first tap.',
            },
          ].map((val, i) => (
            <div
              key={i}
              className="flex flex-col gap-4 p-8 rounded-2xl border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300 bg-white"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-50">
                <i className={`${val.icon} text-emerald-500 text-2xl`} />
              </div>
              <h3 className="font-syne font-semibold text-gray-900 text-lg">{val.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{val.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
